import { useSelector } from '@common/performance/useSelector';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { ExtractArrayType } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import { Table, Checkbox, Stack, StackItem, ColumnsType } from '@prophecy/ui-v3';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { useId } from 'react';

import { Header } from '../Header';
import { TransformationEditor } from '../TransformationEditor';
import {
  LineageState,
  DatasetDetailedView,
  UpstreamTransformation,
  DownstreamTransformation,
  ViewType
} from '../types';

const SchemaTransformations = () => {
  const dataset = useSelector<LineageState, DatasetDetailedView | undefined>((state) => state.metadata.dataset);
  const nullableId = useId();

  const columns: ColumnsType<ExtractArrayType<DatasetDetailedView['columns']>> = [
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Name',
      render: renderColumnWithEllipsis
    },
    {
      key: 'dataType',
      dataIndex: 'dataType',
      title: 'Type',
      render: renderColumnWithEllipsis
    },
    {
      key: 'description',
      dataIndex: 'description',
      title: 'Description',
      render: renderColumnWithEllipsis
    },
    {
      key: 'optional',
      title: <span id={nullableId}>Nullable</span>,
      dataIndex: 'optional',
      render: (value: boolean) => <Checkbox disabled={true} checked={value} aria-labelledby={nullableId} />
    },
    {
      key: 'upstreamTransformations',
      dataIndex: 'upstreamTransformations',
      title: 'Upstream',
      width: '30%',
      render: (upstreamTransformations: UpstreamTransformation[]) => (
        <TransformationEditor
          transformations={upstreamTransformations || (FROZEN_EMPTY_ARRAY as UpstreamTransformation[])}
          language={BackEndCodeLanguage.sql}
          isTable={true}
        />
      )
    },
    {
      key: 'downstreamTransformations',
      width: '30%',
      dataIndex: 'downstreamTransformations',
      title: 'Downstream',
      render: (downstreamTransformations: DownstreamTransformation[]) => (
        <TransformationEditor
          transformations={downstreamTransformations || (FROZEN_EMPTY_ARRAY as DownstreamTransformation[])}
          language={BackEndCodeLanguage.sql}
          isTable={true}
        />
      )
    }
  ];
  const data = dataset?.columns || [];
  return (
    <Stack height='100%' width='100%' padding={theme.spaces.x40}>
      <Table
        data-test-id='dataset-view-table'
        columns={columns}
        width='100%'
        height='100%'
        fixedHeader={true}
        virtualize={true}
        rowKey='name'
        dataSource={data}
      />
    </Stack>
  );
};

export function DatasetView() {
  return (
    <StackItem grow='1'>
      <Stack gap={theme.spaces.x12} height='100%' width='100%'>
        <Header viewType={ViewType.dataset} />
        <SchemaTransformations />
      </Stack>
    </StackItem>
  );
}
